// NewPreemie local storage utility

// Event emitter for storage changes
const storageChangeListeners: Set<() => void> = new Set();

export const onStorageChange = (callback: () => void) => {
  storageChangeListeners.add(callback);
  return () => {
    storageChangeListeners.delete(callback);
  };
};

const notifyStorageChange = () => {
  storageChangeListeners.forEach(callback => callback());
};

// Function to get prefix from URL
const getAppPrefix = (): string | null => {
    const pathParts = window.location.pathname.split('/').filter(part => part);
    // The app name should be the second part of the path: /[hospital-name]/[app-name]/...
    if (pathParts.length >= 2) {
      return pathParts[1];
    }
    return null;
};

// Base key names without prefix
const BASE_CURRENT_KEY = 'current_new_preemie_state_v1';
const HOURS_24 = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Function to get prefixed key
const getCurrentKey = (): string => {
  const prefix = getAppPrefix();
  return prefix ? `${prefix}_${BASE_CURRENT_KEY}` : BASE_CURRENT_KEY;
};

// Timestamp interface for checkbox tracking
interface ChecklistItemTimestamps {
  [key: string]: string | undefined; // ISO timestamp string
}

export interface NewPreemieState {
  // Opening conditions
  anamnesis?: string;
  birthReason?: string;
  gestationalAgeWeeks?: number | null;
  gestationalAgeDays?: number | null;
  weightAssessment?: number | null;
  gender?: 'זכר' | 'נקבה' | null;

  // Medications
  medications?: {
    prenatalSteroids?: boolean;
    prenatalSteroidsOption?: 'one' | 'two' | null;
    neuroprotectiveMagnesium?: boolean;
    maternalAntibiotics?: boolean;
    maternalAntibioticsOption?: 'gbs_under4h' | 'gbs_over4h' | 'broad_under4h' | 'broad_over4h' | 'none' | null;
  };

  // Room Equipment
  roomEquipment?: {
    heatedIncubator?: boolean;
    roomHeating?: boolean;
    heatingBags?: boolean;
    caesareanNotice?: boolean;
    chemicalMattress?: boolean;
    warmSheets?: boolean;
    portableBlower?: boolean;
    babyHat?: boolean;
  };

  // Ventilation Equipment
  ventilationEquipment?: {
    pulseSaturationSensor?: boolean;
    mask?: boolean;
    oxygenConcentration?: boolean;
    suctionEquipment?: boolean;
  };

  // Intubation Equipment
  intubationEquipment?: {
    laryngoscope?: boolean;
    blade?: boolean;
    tubes?: boolean;
    nasalIntubation?: boolean;
    oralIntubation?: boolean;
    lubricant?: boolean;
    fixation?: boolean;
    tubeDepth?: boolean;
    portableVentilator?: boolean;
    portableMonitor?: boolean;
  };

  // Department Admission
  departmentAdmission?: {
    admissionTime?: string;
    birthWeight?: number | null;
    monitoring?: boolean;
    bloodPressure?: boolean;
    temperature?: boolean;
    respiratorAdjustment?: boolean;
    ventilationMethod?: boolean;
    initialMAP?: boolean;
    amp?: boolean;
    minimalOxygen?: boolean;
    surfactant?: boolean;
    ampicillin?: boolean;
    gentamicin?: boolean;
    caffeineOnAdmission?: boolean;
    caffeineAfter24Hours?: boolean;
    fentanyl?: boolean;
    glucoseLevel?: boolean;
    bloodGas?: boolean;
    bloodCount?: boolean;
    bloodCulture?: boolean;
    bloodType?: boolean;
    g6pd?: boolean;
    urineCMV?: boolean;
  };

  // Apgar scores
  apgar?: {
    pulse?: { min1?: number; min5?: number; min10?: number };
    breathing?: { min1?: number; min5?: number; min10?: number };
    muscleTone?: { min1?: number; min5?: number; min10?: number };
    skinColor?: { min1?: number; min5?: number; min10?: number };
    touchResponse?: { min1?: number; min5?: number; min10?: number };
  };

  // Apgar Timer
  apgarTimer?: {
    startTime: number | null;
    isRunning: boolean;
    notifiedMinutes: number[];
  };

  // Communication with the Parents
  parents?: {
    preemieStatus?: boolean;
    explanations?: boolean;
    giveBloodConsent?: boolean;
  };

  // Umbilical Cord
  umbilicalCord?: {
    option?: 'one_minute' | 'less_than_minute' | 'not_performed' | null;
    reason?: string;
  };

  // Nutrition
  nutrition?: {
    // Placeholder for future nutrition-related fields
  };

  // Timestamps for checklist items (for PDF export start times)
  medicationsTimestamps?: ChecklistItemTimestamps;
  roomEquipmentTimestamps?: ChecklistItemTimestamps;
  ventilationEquipmentTimestamps?: ChecklistItemTimestamps;
  intubationEquipmentTimestamps?: ChecklistItemTimestamps;
  departmentAdmissionTimestamps?: ChecklistItemTimestamps;
  parentsTimestamps?: ChecklistItemTimestamps;

  lastSaved?: string;
}

export const saveCurrentState = (state: Partial<NewPreemieState>, section?: keyof NewPreemieState): void => {
  try {
    const currentState = (loadCurrentState() || {}) as Partial<NewPreemieState>;
    
    let newState: Partial<NewPreemieState>;
    
    // If a specific section is provided, only update that section
    if (section) {
      newState = {
        ...currentState,
        [section]: {
          ...(typeof currentState[section] === 'object' ? currentState[section] : {}),
          ...(typeof state[section] === 'object' ? state[section] : {})
        }
      };
    } else {
      // Otherwise merge the entire state
      newState = {
        ...currentState,
        ...state
      };
    }

    // Update lastSaved timestamp
    newState.lastSaved = new Date().toISOString();

    localStorage.setItem(getCurrentKey(), JSON.stringify(newState));
    
    // Notify listeners of the change
    notifyStorageChange();
  } catch (error) {
    console.error('Error saving current NewPreemie state:', error);
  }
};

export const loadCurrentState = (): NewPreemieState | null => {
  try {
    const savedState = localStorage.getItem(getCurrentKey());
    if (!savedState) {
      return null;
    }

    const state = JSON.parse(savedState) as NewPreemieState;
    
    // Check if 24 hours have passed since last save
    if (state.lastSaved) {
      const lastSaved = new Date(state.lastSaved).getTime();
      const now = new Date().getTime();
      
      if (now - lastSaved > HOURS_24) {        
        clearCurrentState(); // Clear expired state
        return null;
      }
    }

    return state;
  } catch (error) {
    console.error('Error loading current NewPreemie state:', error);
    return null;
  }
};

export const clearCurrentState = (): void => {
  try {
    localStorage.removeItem(getCurrentKey());
  } catch (error) {
    console.error('Error clearing NewPreemie state:', error);
  }
};
